import { useEffect } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Spinner } from 'flowbite-react';

/**
 * SSO Landing Page for external identity provider initiated logins (e.g., ClassLink).
 *
 * When external systems like ClassLink need to send users to this application,
 * they should link to this page instead of directly to Auth0's /authorize endpoint.
 * This ensures proper PKCE flow is initiated by the Auth0 SDK.
 *
 * Usage:
 *   https://directory-staging.aiu3.net/sso?connection=AIUStaffSingleSignOn
 *
 * Query Parameters:
 *   - connection: (optional) The Auth0 connection name to use (e.g., "AIUStaffSingleSignOn")
 *                 If provided, bypasses the Universal Login and goes directly to that IdP.
 */
export const SSOLandingPage: React.FC = () => {
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
  const [searchParams] = useSearchParams();

  const connection = searchParams.get('connection');

  useEffect(() => {
    // If already authenticated, no need to redirect to Auth0
    if (isAuthenticated || isLoading) {
      return;
    }

    // Initiate proper OAuth flow with PKCE
    console.log('[SSO] Initiating login with connection:', connection || 'default');

    loginWithRedirect({
      appState: { returnTo: '/' },
      authorizationParams: connection ? { connection } : undefined,
    });
  }, [isAuthenticated, isLoading, loginWithRedirect, connection]);

  // If already authenticated, redirect to home
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Show loading while redirecting to Auth0
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <Spinner size="xl" color="info" />
        <p className="mt-4 text-gray-600">Redirecting to sign in...</p>
      </div>
    </div>
  );
};

export default SSOLandingPage;
